import { groupBySection, packColumns, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent } from '../types.js';
import { featuredItem } from './cafe-panels.js';
import {
  badge,
  bilingual,
  description,
  fitScale,
  price,
  sellingPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import {
  BB_FAMILY_ID,
  BB_FAMILY_NAME,
  BB_THEME,
  BB_TORN,
  BB_VARIANTS,
  bbBannerCss,
  bbColumn,
  bbHeader,
  bbHeaderCss,
  bbHeaderHeight,
  bbPhoto,
  bbPhotoCss,
  bbRootCss,
  bbRowsCss,
  bbSection,
} from './brush-burger-board.js';

/**
 * Brush Burger, layout C: the featured burger as a big torn round photo with its price painted
 * on a brush splat and its name on a brush banner; the rest of the menu beside it as a
 * two-column brush list under the title.
 */

const S = '.mb-t-brush-burger-hero';
const TITLE_EM = 5.8;
/** Height the hero takes above the list in portrait. */
const PORTRAIT_HERO = 33;

function columns(board: MenuBoardContent, portrait: boolean, hero: boolean): MenuColumn[] {
  const header = bbHeaderHeight(board, TITLE_EM, 1) + 1.8;
  const capacity = portrait ? 120 - 4 - header - (hero ? PORTRAIT_HERO + 2 : 0) : 67.5 - 4 - header;
  const perLine = hero && !portrait ? 46 : 50;
  return [bbColumn(board, capacity, perLine), bbColumn(board, capacity, perLine)];
}

function heroPrice(ctx: RenderContext, item: MenuItemContent): string {
  if (!item.variants.length) {
    // The splat is round: long prices are set smaller to stay inside it.
    const text = price(ctx, item.discountPrice ?? item.price);
    return `<span class="bb3-hfit" style="font-size:${fitScale(text, 7, 0.5).toFixed(3)}em">${sellingPrice(ctx, item, 'bb3-hp')}</span>`;
  }
  return `<span class="bb3-hsizes">${item.variants
    .map((v) => `<span class="bb3-hsize"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
    .join('')}</span>`;
}

function hero(ctx: RenderContext, item: MenuItemContent): string {
  const name = bilingual(ctx, item.title, item.titleAr, 'bb3-hname');
  const badges = badge(ctx, item, 'bb3-hbadge');
  const scale = fitScale(item.title, 16, 0.6);
  return [
    '<div class="bb3-hero">',
    '<div class="bb3-stage">',
    bbPhoto(ctx, item.imageMediaId, 'bb3-photo') || '<div class="bb3-photo bb3-nophoto"></div>',
    `<div class="bb3-splat${item.variants.length ? ' bb3-splat-sizes' : ''}">${heroPrice(ctx, item)}</div>`,
    badges ? `<div class="bb3-hbadges">${badges}</div>` : '',
    '</div>',
    '<div class="bb3-htext">',
    `<div class="bb3-hbanner" style="font-size:${scale.toFixed(3)}em">${name}</div>`,
    description(ctx, item, 'bb3-hdesc'),
    '</div></div>',
  ].join('');
}

export const brushBurgerHero: MenuTemplate = {
  id: 'brush-burger-hero',
  name: 'Brush hero burger',
  description:
    'Grunge black burger board with your featured burger large: a torn round photo, its price on a brush splat and its name on a brush banner, beside a two-column list.',
  defaultTheme: BB_THEME,
  variants: BB_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { id: BB_FAMILY_ID, name: BB_FAMILY_NAME, layout: 'Hero and list' },
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), columns(board, orientation === 'portrait', Boolean(featured)), {
      balance: true,
    });
  },

  render(ctx) {
    const item = featuredItem(ctx.board);
    const lists = ctx.page.columns
      .map((c) => `<div class="bb3-col">${c.map((b) => bbSection(ctx, b, 'bb3')).join('')}</div>`)
      .join('');
    return [
      `<div class="bb3-grid${item ? '' : ' bb3-no-hero'}">`,
      item ? hero(ctx, item) : '',
      bbHeader(ctx, 'bb3', 18, true),
      `<div class="bb3-cols">${lists}</div>`,
      '</div>',
    ].join('');
  },

  css: `
${bbRootCss(S)}
${S}{padding:2em 3em}
${S} .bb3-grid{flex:1;min-height:0;display:grid;grid-template-columns:minmax(0,40fr) minmax(0,60fr);grid-template-rows:auto 1fr;
  grid-template-areas:"hero head" "hero list";column-gap:3.5em}
${S} .bb3-no-hero{grid-template-columns:minmax(0,1fr);grid-template-areas:"head" "list"}
${S}.mb-portrait .bb3-grid{grid-template-columns:minmax(0,1fr);grid-template-rows:auto auto 1fr;grid-template-areas:"head" "hero" "list"}
${S}.mb-portrait .bb3-no-hero{grid-template-rows:auto 1fr;grid-template-areas:"head" "list"}

${bbHeaderCss(S, 'bb3', TITLE_EM)}
${S} .bb3-header{grid-area:head;text-align:start;margin-bottom:1.8em}
${S} .bb3-brand{transform-origin:left center}
${S} .bb3-title{-webkit-line-clamp:1;white-space:nowrap}
${S} .bb3-tagline{letter-spacing:.2em}
${S} .bb3-header .mb-2nd{text-align:left}
${S} .bb3-no-hero .bb3-header,${S}.mb-portrait .bb3-header,${S} .bb3-no-hero .bb3-header .mb-2nd,${S}.mb-portrait .bb3-header .mb-2nd{text-align:center}
${S} .bb3-no-hero .bb3-brand,${S}.mb-portrait .bb3-brand{transform-origin:center}

${S} .bb3-cols{grid-area:list;min-height:0;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:3em}
${S} .bb3-col{min-width:0;display:flex;flex-direction:column}
${bbRowsCss(S, 'bb3')}

${S} .bb3-hero{grid-area:hero;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;position:relative}
${S}.mb-portrait .bb3-hero{flex-direction:row;gap:3em;height:${PORTRAIT_HERO}em;margin-bottom:2em}
${S} .bb3-stage{position:relative;width:35em;max-width:100%}
${S}.mb-portrait .bb3-stage{width:30em;flex:0 0 auto}
${bbPhotoCss(S, 'bb3-photo')}
${S} .bb3-photo{width:100%}
${S} .bb3-nophoto::after{content:'';position:absolute;inset:0;background:var(--mb-surface);
  -webkit-mask:${BB_TORN} center/100% 100% no-repeat;mask:${BB_TORN} center/100% 100% no-repeat}
${S} .bb3-splat{position:absolute;z-index:2;top:-1em;right:-2.5em;width:13.5em;aspect-ratio:1;display:flex;flex-direction:column;
  align-items:center;justify-content:center;background:var(--mb-accent);color:var(--mb-bg);transform:rotate(10deg);
  -webkit-mask:${BB_TORN} center/100% 100% no-repeat;mask:${BB_TORN} center/100% 100% no-repeat}
${S}.mb-portrait .bb3-splat{width:12em;right:-1.5em}
${S} .bb3-hfit{display:flex;flex-direction:column;align-items:center}
${S}.mb-portrait .bb3-hp{font-size:3.7em}
${S} .bb3-hp{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:4.3em;line-height:1;letter-spacing:.02em;white-space:nowrap}
${S} .bb3-hp-old{font-size:1.2em;opacity:.7;margin-top:.15em}
${S} .bb3-hsizes{display:flex;flex-direction:column;align-items:center;gap:.15em}
${S} .bb3-hsize{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.3em;line-height:1;white-space:nowrap}
${S} .bb3-hsize small{font-size:.55em}
${S} .bb3-hbadges{position:absolute;z-index:2;left:0;bottom:4em;display:flex;flex-direction:column;align-items:flex-start;gap:.3em}
${S} .bb3-hbadge{font-size:1.1em;font-weight:800;padding:.2em .7em;border-radius:.2em;background:var(--mb-primary);color:var(--mb-bg);transform:rotate(-8deg);white-space:nowrap}
${S} .bb3-htext{position:relative;z-index:3;display:flex;flex-direction:column;align-items:center;text-align:center;max-width:100%;margin-top:-3.2em}
${S}.mb-portrait .bb3-htext{margin-top:0;flex:1 1 auto;min-width:0}
${bbBannerCss(S, 'bb3-hbanner')}
${S} .bb3-hbanner{max-width:100%;padding:.8em 1.4em .65em;transform:rotate(-2deg)}
${S} .bb3-hname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:4.2em;line-height:1.1;letter-spacing:.05em;
  text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .bb3-hname.mb-2nd{font-size:2em;letter-spacing:0;margin-top:-.1em}
${S} .bb3-hdesc{font-size:1.2em;line-height:1.35;max-width:26em;margin-top:.9em;opacity:.92;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .bb3-hdesc.mb-2nd{margin-top:.3em}
`,
};
